/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { ICategoryScopeFacade } from '../../../../../data-access/src/lib/domain/facades-models/category-scope-facade-interface';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { TooltipItemInfo } from '../../../../../utils/src/lib/common-helper/tooltip-item-info/tooltip-item-info';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { BackendService } from '../../../../../utils/src/lib/sysque-backend/backend.service';
import { TooltipItem } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tooltip-item';
import { GlobalConstants } from '../../constants/global-constants';
import { MenuItemProperties } from '../../models/menu-item-properties';
import { FittingDefaultReturnCodes } from '../../models/fitting-default-return-codes';
import { FittingEnableDisableReturnCodes } from '../../models/fitting-enable-disable-return-codes';
import { DeleteItemReturnCodes } from '../../models/delete-item-return-codes';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { Store } from '@ngrx/store';
import { sendPopupRoutingAction } from '../../+state/toolbelt.actions';
import { GetAddFamilyInformationDialogType } from '../add-family-information/add-family-information-actions';
import { AddFamilyInformationTypeDialog, InputData } from '../../models/family-information-model';
import { ReceiveMessageHandler } from '@mep/web-browser-bridge';
import { AddFittingsReceiveController } from '../../receive-controllers/add-fittings-receive-controller';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { DockableDrawingObject } from '../../models/dockable-drawing-object';
import { IFamilyInformationItem } from '../../components/add-family-information/add-family-information-types';
import { SysQueStateService } from '../../../../../utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';
import { RightClickMenuUpdater } from '../../../../../utils/src/lib/common-helper/right-click-menu-updater/right-click-menu-updater';
import { SystemStateManager } from '../../../../../utils/src/lib/system-state-manager.service';
import { ItemReorderInfo } from '../../../../../data-access/src/lib/domain/item-reorder-info';

@Injectable({
  providedIn: 'root',
})
export class CategoryScopeFacade implements ICategoryScopeFacade {
  windowKey: BehaviorSubject<string> = new BehaviorSubject<string>('');
  constructor(
    private readonly receiveMessageHandler: ReceiveMessageHandler,
    private localStorageService: LocalStorageService,
    private backendService: BackendService,
    private store: Store,
    private sysqueStateService: SysQueStateService,
    private stateMangerSvc: SystemStateManager
  ) {}

  public isTooltipOn(): Observable<boolean> {
    return from<Promise<boolean>>(this.backendService.messageHandler.sendRequestAsync(GlobalConstants.IsTooltipOn));
  }

  public updateManageDestStateFittingListPropertyForCopiedItem(state: SysqueElectricalState, item: Item): void {
    let currentState = this.sysqueStateService.getManageDestState();
    if (currentState.selectedSystem === state.selectedSystem) {
      currentState.fittingList.find((fitting) => fitting.id === item.id).isCopyState = false;
      this.sysqueStateService.updateDestProperty('fittingList', state.fittingList);
    } else {
      state.fittingList.find((fitting) => fitting.id === item.id).isCopyState = true;
    }  
  }

  public updateManageDestStateFittingListProperty(state: SysqueElectricalState): void {
    const currentState = this.sysqueStateService.getManageDestState();
    if (currentState.selectedSystem === state.selectedSystem) {
      this.sysqueStateService.updateDestProperty('fittingList', state.fittingList);
    }
  }

  public updateManageSrcStateFittingListProperty(srcState: SysqueElectricalState): void {
    this.sysqueStateService.updateSrcProperty('fittingList', srcState.fittingList);
  }

  public getManageSrcState(): SysqueElectricalState {
    return this.sysqueStateService.getManageSrcState();
  }

  public getDockedState(): SysqueElectricalState {
    return this.stateMangerSvc.getDockedModeState();
  }

  public updateLocalStorageForEditDuplicateAlert(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }

  public registerControllers(): void {
    if (this.windowKey.value === 'adf' || this.windowKey.value === 'src') {
      this.receiveMessageHandler.registerController(
        new AddFittingsReceiveController(this.localStorageService, this.sysqueStateService)
      );
    }
  }

  public getSysqueConnectState(): boolean {
    return this.localStorageService.getStorage(GlobalConstants.IS_SYSQUE_CONNECT_ON);
  }

  public sendDrawingOptions(
    drawingAction: string,
    drawingObjectProperties: DockableDrawingObject
  ): Observable<boolean> {
    return from<Promise<boolean>>(
      this.backendService.messageHandler.sendRequestAsync(drawingAction, [drawingObjectProperties])
    );
  }

  public getLocalStorageUpdate(): BehaviorSubject<string> {
    return this.localStorageService.localStorageUpdate$;
  }

  public getLocalStorage(key: string): SysqueElectricalState | any {
    return this.localStorageService.getStorage(key);
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }

  public menuItemsUpdate(
    item: Item,
    defaultCategory: string,
    windowName: string,
    selectedCategory: string,
    gaugeVariable: string,
    releaseVersion: string
  ): any {
    return new RightClickMenuUpdater().menuItemsUpdate(
      item,
      defaultCategory,
      windowName,
      selectedCategory,
      gaugeVariable,
      releaseVersion
    );
  }

  public downloadFitting(itemId: string, releaseVersion: string, selectedSystem?: string): Observable<boolean> {
    if (releaseVersion && releaseVersion !== '11.4') {
      return from<Promise<boolean>>(
        this.backendService.messageHandler.sendRequestAsync(GlobalConstants.DownloadFitting, [itemId, selectedSystem])
      );
    }

    return from<Promise<boolean>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.DownloadFitting, [itemId])
    );
  }

  public getFittings(systemName: string, categoryName: string): Promise<Item[]> {
    return this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetFittingsBySystemNameAndCategory, [
      systemName,
      categoryName,
      false,
    ]);
  }

  public getTooltipItemInfo(item: Item, category: string): TooltipItem[] {
    return new TooltipItemInfo().getTooltipItemInfo(item, category);
  }

  public setRemoveDefaultFitting(
    menuItemFitting: MenuItemProperties,
    isDefault: boolean
  ): Promise<FittingDefaultReturnCodes> {
    return this.backendService.setRemoveDefaultFitting(menuItemFitting, isDefault);
  }

  public enableDisableFitting(menuItemFitting: MenuItemProperties): Promise<FittingEnableDisableReturnCodes> {
    return this.backendService.enableDisableFitting(menuItemFitting);
  }

  public deleteItem(menuItemProperties: MenuItemProperties): Observable<DeleteItemReturnCodes> {
    return this.backendService.deleteItem(menuItemProperties);
  }

  public getSystemState(): any {
    return this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
  }

  public getAddFamilyInformationDialogType(item: IFamilyInformationItem): void {
    //set selected system
    const selectedSystem =
      this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY).selectedSystem ?? '';
    item.systemName = selectedSystem;
    const selectedCategory =
      this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY).selectedCategory ?? '';
    item.shape = selectedCategory;
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);
    //dispatch action to get dialog type
    let inputData = new InputData(item);
    this.store.dispatch(GetAddFamilyInformationDialogType({ data: inputData }));
  }

  public createAddFamilyInformationBasedOnItemType(dialogType: AddFamilyInformationTypeDialog): void {
    let sysquePopupConfig = new SysquePopupConfig();
    switch (dialogType) {
      case AddFamilyInformationTypeDialog.CableTrayDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.CABLE_TRAY_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.CABLE_TRAY_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      case AddFamilyInformationTypeDialog.ConduitDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.CONDUIT_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.CONDUIT_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      case AddFamilyInformationTypeDialog.JunctionBoxFamilyInformationDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.JUNCTION_BOX_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.JUNCTION_BOX_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      case AddFamilyInformationTypeDialog.FieldFabDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.FIELD_FAB_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.FIELD_FAB_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      case AddFamilyInformationTypeDialog.HandAndMachineBendFamilyInformationDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.HAND_AND_MACHINE_BEND_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.HAND_AND_MACHINE_BEND_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      case AddFamilyInformationTypeDialog.KickHandAndMachineBendFamilyInformationDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.KICK_HAND_AND_MACHINE_BEND_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.KICK_HAND_AND_MACHINE_BEND_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      case AddFamilyInformationTypeDialog.UserCustomContentDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.USER_CUSTOM_CONTENT_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.USER_CUSTOM_CONTENT_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      case AddFamilyInformationTypeDialog.HangerSpecificationDialog:
        console.log('Before making the request to backend to open the dialog');
        createDialog(sysquePopupConfig, GlobalConstants.HANGER_SPECIFICATION_FAMILY_INFORMATION_DIALOG);
        this.store.dispatch(
          sendPopupRoutingAction({
            route: GlobalConstants.HANGER_SPECIFICATION_FAMILY_INFORMATION_DIALOG,
            width: sysquePopupConfig.width,
            height: sysquePopupConfig.height,
          })
        );
        break;
      default:
        break;
    }
  }

  public saveNewFittingsOrder(items: ItemReorderInfo[], systemName: string, categoryName: string): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.SaveNewFittingsOrder, [
      items,
      systemName,
      categoryName,
    ]);
  }

  public showReportIssue(fittingId: string): Observable<any> {
    return from<Promise<any>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.ShowReportIssue, [fittingId])
    );
  }
}
